import { useEffect, useId, useRef, useState } from 'react'
import clsx from 'clsx'
import { ChevronDown, ChevronUp, Copied, Copy, Skill, Sparkles } from '@/assets/icons'
import type { MatterArtifactVersionSelection } from '@/agent-v2/store/schemas'
import { MessageCopyStatus } from '@/agent-v2/components/MessageCopyStatus'
import { useMessageCopy } from '@/agent-v2/hooks/useMessageCopy'
import { copyPlainText } from '@/agent-v2/utils/copy-plain-text'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { IconButton } from '@/components/icon-button/IconButton'
import { useViewportSizeInPixels } from '@/hooks/use-viewport-size-in-pixels'
import { isSkillOnlyFallbackMessage } from '@/user-skills/utils/resolve-composer-submit-text'

const kCollapsedMaxHeightPx = 240

export type UserMessageBubbleProps = {
  artifactName?: string
  matterArtifactSelection?: MatterArtifactVersionSelection
  text: string
  attachedSkillNames?: string[]
  collapsedMaxHeightPx?: number
}

export function UserMessageBubble({
  artifactName = 'Matter artifact',
  matterArtifactSelection,
  text,
  attachedSkillNames,
  collapsedMaxHeightPx = kCollapsedMaxHeightPx,
}: UserMessageBubbleProps) {
  const textId = useId()
  const [isExpanded, setIsExpanded] = useState(false)
  // undefined until measured: avoids showing the toggle before we know the text overflows the collapsed height
  const [overflows, setOverflows] = useState<boolean | undefined>(undefined)
  const textRef = useRef<HTMLDivElement>(null)
  const { width: viewportWidth } = useViewportSizeInPixels()
  const hasAttachedSkills = Boolean(attachedSkillNames?.length)
  // Skill-only turns still send a backend placeholder; hide that placeholder when chips convey the intent.
  const hideSkillOnlyFallbackBody = isSkillOnlyFallbackMessage(text, attachedSkillNames)
  const { isCopying, isCopied, copyError, handleCopy } = useMessageCopy({
    copy: () => copyPlainText(text),
    analyticsEvent: AnalyticsEvent.CopyChatUserMessage,
    errorMessage: 'Message could not be copied. Try again.',
  })

  // Only the collapsed view is height-clamped, so only measure then — re-checks on resize, and
  // re-validates whenever the user collapses back after a resize that happened while expanded.
  useEffect(() => {
    if (isExpanded || hideSkillOnlyFallbackBody) return
    const el = textRef.current
    if (!el) return
    setOverflows(el.scrollHeight > el.clientHeight)
  }, [isExpanded, viewportWidth, text, attachedSkillNames, hideSkillOnlyFallbackBody])

  return (
    <div className="ml-auto w-fit max-w-[680px]">
      <div className="agent-v2-user-message relative overflow-hidden rounded-lg bg-brand-neutral-100">
        {hasAttachedSkills && (
          <div
            className={clsx('mx-3 mt-3 flex flex-wrap items-center gap-1.5 text-sm leading-5 text-brand-neutral-600', hideSkillOnlyFallbackBody && 'mb-3')}
            aria-label="Attached skills"
          >
            {attachedSkillNames!.map((name, index) => (
              <span key={index} className="inline-flex min-w-0 max-w-full items-stretch rounded-lg border border-brand-neutral-200 bg-brand-neutral-50">
                <span className="flex shrink-0 items-center border-r border-brand-neutral-200 p-2">
                  <Skill aria-hidden className="h-5 w-5" />
                </span>
                <span className="min-w-0 self-center break-words py-1 pl-2 pr-3 font-rethink-sans font-medium">{name}</span>
              </span>
            ))}
          </div>
        )}
        {matterArtifactSelection ? (
          <div className="mx-3 mt-3 border-b border-brand-neutral-300 pb-2">
            <div className="flex items-center gap-1.5 text-xs font-medium leading-4 text-brand-neutral-800">
              <Sparkles aria-hidden="true" className="size-4 shrink-0 text-brand-neutral-700" />
              Selected from {artifactName}
            </div>
          </div>
        ) : null}
        {!hideSkillOnlyFallbackBody && (
          <div
            ref={textRef}
            id={textId}
            className="overflow-hidden p-3 text-sm leading-[1.5] tracking-[-0.15px] text-brand-neutral-950 whitespace-pre-wrap compatible-word-break"
            style={isExpanded ? undefined : { maxHeight: collapsedMaxHeightPx }}
          >
            {text}
          </div>
        )}
        {overflows && !hideSkillOnlyFallbackBody && (
          <div className={isExpanded ? 'px-1 pb-2' : 'pointer-events-none absolute inset-x-0 bottom-0 flex h-12 items-end pb-2 pl-1'}>
            {!isExpanded && (
              <div className="absolute inset-x-0 -top-[13px] bottom-0 rounded-b-lg bg-gradient-to-b from-transparent to-brand-neutral-100 to-40%" />
            )}
            <button
              type="button"
              aria-expanded={isExpanded}
              aria-controls={textId}
              onClick={() => setIsExpanded((expanded) => !expanded)}
              className="pointer-events-auto relative flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-brand-neutral-950 hover:bg-brand-neutral-200"
            >
              {isExpanded ? 'Show less' : 'Show more'}
              {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          </div>
        )}
      </div>
      <div className="mt-2 flex items-center gap-1">
        <IconButton
          aria-label="Copy message to clipboard"
          color="neutral"
          icon={isCopied ? <Copied /> : <Copy />}
          loading={isCopying}
          onClick={handleCopy}
          size="sm"
          tooltipPlacement="bottom"
          tooltipText={isCopied ? 'Copied' : 'Copy'}
          type="button"
          variant="plain"
        />
        <MessageCopyStatus isCopied={isCopied} />
      </div>
      {copyError && (
        <p className="mt-1 text-xs text-red-700" role="alert">
          {copyError}
        </p>
      )}
    </div>
  )
}
